import React from "react";
import { components, GroupBase, MenuProps, NoticeProps, Props as SelectProps } from "react-select";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Button from "./Button";
import { lookupCreateUrl } from "../utils/lookupUrl";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { isPermissionGranted } from "../utils/navPermissions";
import { menuItems } from "../layouts/navMenu";

/**
 * Lookup selects (user pick 2026-10-05, "option A"): the field at rest shows only
 * its value and caret. The OPEN list carries the lookup actions — a footer with
 * "Add new <thing>" (its create page in a new tab) and "Reload list", and a
 * search with no results offers "Add "<query>" as a new <thing>". Help sits in
 * the notched label (FieldHelp).
 *
 * Any react-select gets this by spreading `lookupSelectProps(actions)` into it;
 * the custom Menu / NoOptionsMessage read the actions from `selectProps`.
 */
export interface LookupActions {
    /** What one entry is called, lower case: "currency", "service country". */
    noun: string;
    /** The lookup's LIST route; "Add new" opens its CREATE page. */
    adminPageUrl?: string;
    /** Explicit create route for irregular / nested lookups. */
    createPageUrl?: string;
    /** Re-fetches the options. Also runs when the user comes back from the create tab. */
    onRefresh?: () => void;
}

declare module "react-select/dist/declarations/src/Select" {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
        /** Lookup actions shown in the open list (LookupMenu). */
        lookupActions?: LookupActions;
    }
}

/** "Service Country" → "service country"; acronyms keep their case ("VAT Code" → "VAT code"). */
export const lookupNoun = (label: string | null | undefined): string =>
    (label ?? "entry")
        .replace(/\s*\*\s*$/, "")
        .trim()
        .split(/\s+/)
        .map((word) => (word.length > 1 && word === word.toUpperCase() ? word : word.toLowerCase()))
        .join(" ") || "entry";

/** One of a plural name: "currencies" → "currency", "job positions" → "job position", "statuses" → "status". */
export const singularNoun = (noun: string): string => {
    if (/ies$/.test(noun)) return noun.replace(/ies$/, "y");
    if (/(ss|us|is)$/.test(noun)) return noun;
    if (/(ss|us|x|ch|sh)es$/.test(noun)) return noun.replace(/es$/, "");
    return noun.replace(/s$/, "");
};

/**
 * What one entry is called in the list's actions ("Add new <noun>"). The admin
 * page names the kind of record ("Currencies" → "currency"); a label often
 * names the field's role instead ("Received into", "Billing currency"), so it's
 * the fallback.
 */
export const lookupEntryNoun = (label: string | null | undefined, adminPageLabel?: string | null): string => {
    if (!adminPageLabel) return lookupNoun(label);
    if (/^chart of accounts$/i.test(adminPageLabel.trim())) return "account";
    // "Service Category Types" is the list of service categories
    return singularNoun(lookupNoun(adminPageLabel)).replace(/\bcategory type$/, "category");
};

const hasActions = (actions?: LookupActions): actions is LookupActions => !!actions && !!(actions.adminPageUrl || actions.createPageUrl || actions.onRefresh);

/** The create permission behind a lookup's admin page, read from the nav ("/currencies" → "Lookups:create"). */
const createPermissionFor = (adminPageUrl?: string): string | null => {
    const path = adminPageUrl?.split(/[?#]/)[0];
    if (!path) return null;
    for (const category of menuItems) {
        const item = category.items.find((entry) => entry.to === path);
        if (item?.permission) return `${item.permission.split(":")[0]}:create`;
    }
    return null;
};

/**
 * Whether this user may add an entry to a lookup (create on its admin page).
 * Without it "Add new" is hidden, not offered and then denied. A page the nav
 * doesn't list is left to its own route guard.
 */
export const useCanAddLookup = (adminPageUrl?: string): boolean => {
    const { currentUser } = useUserPermissions();
    const permission = createPermissionFor(adminPageUrl);
    return !permission || isPermissionGranted(currentUser?.pages ?? [], permission);
};

/** Opens the create page in a new tab and reloads the list when the user comes back. */
export const openLookupCreate = (actions: LookupActions) => {
    const url = actions.createPageUrl ?? lookupCreateUrl(actions.adminPageUrl);
    if (!url) return;
    if (actions.onRefresh) {
        const reload = actions.onRefresh;
        window.addEventListener("focus", () => reload(), { once: true });
    }
    window.open(url, "_blank");
};

/** Keep react-select's input focused (and the list open) while a footer button is pressed. */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

const AddNewButton: React.FC<{ actions: LookupActions; query?: string }> = ({ actions, query }) => (
    <Button
        type="button"
        size="xs"
        variant="ghost"
        icon="plus-lg"
        className="lookup-menu-add"
        title={`Opens the new ${actions.noun} form in a new tab. The list reloads when you come back. (Alt+N)`}
        aria-keyshortcuts="Alt+N"
        onMouseDown={keepFocus}
        onClick={() => openLookupCreate(actions)}
    >
        {/* one run of text, so the ↗ follows the last word when a long query wraps */}
        <span>
            {query ? `Add “${query}” as a new ${actions.noun}` : `Add new ${actions.noun}`}
            <i className="bi bi-box-arrow-up-right lookup-menu-ext" aria-hidden="true" />
        </span>
    </Button>
);

/** The menu with the lookup footer under the (scrolling) option list. */
export function LookupMenu<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: MenuProps<Option, IsMulti, Group>) {
    const actions = props.selectProps.lookupActions;
    const canAdd = useCanAddLookup(actions?.adminPageUrl) && !!(actions?.adminPageUrl || actions?.createPageUrl);
    return (
        <components.Menu {...props}>
            {props.children}
            {hasActions(actions) && (canAdd || actions.onRefresh) && (
                <div className="lookup-menu-footer" onMouseDown={keepFocus}>
                    {canAdd && <AddNewButton actions={actions} />}
                    {actions.onRefresh && (
                        <Button
                            type="button"
                            size="xs"
                            variant="ghost"
                            icon="arrow-clockwise"
                            title="Fetch the list again, e.g. after adding an entry in another tab (Alt+R)"
                            aria-keyshortcuts="Alt+R"
                            onMouseDown={keepFocus}
                            onClick={actions.onRefresh}
                        >
                            Reload list
                        </Button>
                    )}
                </div>
            )}
        </components.Menu>
    );
}

/** A search with no results offers to add exactly what was typed. */
export function LookupNoOptionsMessage<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: NoticeProps<Option, IsMulti, Group>) {
    const actions = props.selectProps.lookupActions;
    const query = (props.selectProps.inputValue ?? "").trim();
    const canAdd = useCanAddLookup(actions?.adminPageUrl);
    if (!query || !actions || !canAdd || !(actions.adminPageUrl || actions.createPageUrl)) {
        return <components.NoOptionsMessage {...props} />;
    }
    return (
        <components.NoOptionsMessage {...props}>
            <div className="lookup-menu-empty">
                <span>No {actions.noun} matches “{query}”.</span>
                <AddNewButton actions={actions} query={query} />
            </div>
        </components.NoOptionsMessage>
    );
}

/**
 * Props to spread into a react-select so its open list carries the lookup
 * actions. `base` is the select's own props, so its own `components` and
 * `onKeyDown` keep working.
 */
// The option type is the host select's, so the result is typed loosely and
// spreads into any react-select (single or multi, sync or async).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lookupSelectProps<Option = any, IsMulti extends boolean = boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    actions: LookupActions | undefined,
    base?: Partial<SelectProps<Option, IsMulti, Group>>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Partial<SelectProps<any, any, any>> {
    if (!hasActions(actions)) return {};
    return {
        lookupActions: actions,
        components: { ...(base?.components ?? {}), Menu: LookupMenu, NoOptionsMessage: LookupNoOptionsMessage },
        onKeyDown: (event) => {
            base?.onKeyDown?.(event);
            if (event.defaultPrevented || !event.altKey) return;
            if (event.code === "KeyN" && (actions.adminPageUrl || actions.createPageUrl)) {
                event.preventDefault();
                openLookupCreate(actions);
            } else if (event.code === "KeyR" && actions.onRefresh) {
                event.preventDefault();
                actions.onRefresh();
            }
        },
    };
}

/**
 * The gold ⓘ for a field's help, placed in the notched label (filled) or the
 * placeholder (empty). Pressing it never opens the list.
 */
export const FieldHelp: React.FC<{ id: string; label?: string; text: string }> = ({ id, label, text }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`${id || "field"}-help`} style={{ whiteSpace: "pre-line" }}>{text}</Tooltip>}>
        <i
            className="bi bi-info-circle crx-field-help"
            tabIndex={0}
            role="img"
            aria-label={`${label ?? "Field"} help`}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchEnd={(event) => event.stopPropagation()}
        />
    </OverlayTrigger>
);
